const STORAGE_KEY = 'yesmed-painel-audio-enabled'
const VOICE_NAME = 'Brazilian Portuguese Female'

function activationError(stage, cause) {
  const stageLabel = stage === 'alert' ? 'alerta' : 'voz'
  const detail = [cause?.name, cause?.message].filter(Boolean).join(': ')
  const suffix = detail ? ` (${detail})` : ''
  const error = new Error(`Falha ao ativar o som na etapa de ${stageLabel}${suffix}.`)
  error.name = 'PainelAudioActivationError'
  error.stage = stage
  error.cause = cause
  return error
}

export class PainelAudio {
  constructor({
    createAudio,
    voice,
    storage,
    alertSource,
    timerHost = global,
    setTimer = timerHost.setTimeout,
    clearTimer = timerHost.clearTimeout
  }) {
    this.audio = createAudio(alertSource)
    this.voice = voice
    this.storage = storage
    this.setTimer = setTimer.bind(timerHost)
    this.clearTimer = clearTimer.bind(timerHost)
    this.queue = []
    this.processing = false
    this.queueBlocked = false
    this.destroyed = false
    this.activeTimer = null
    this.activeItem = null
    this.cancelActive = null
    this.restoreOperation = null

    if (this.voice) this.voice.disablePermissionPopup = true
  }

  wasActivated() {
    return this.storage?.getItem(STORAGE_KEY) === 'true'
  }

  async restore() {
    if (this.destroyed || !this.wasActivated()) return false

    this.cancelRestore()
    const operation = {
      cancelled: false,
      cancel: null,
      originalVolume: this.audio.volume
    }
    this.restoreOperation = operation
    try {
      this.audio.volume = 0.01
      if (this.destroyed) return false
      const started = this.audio.play()
      if (started?.then) {
        const completed = await new Promise((resolve, reject) => {
          let settled = false
          const finish = (callback, value) => {
            if (settled) return
            settled = true
            callback(value)
          }
          const cancel = () => finish(resolve, false)

          operation.cancel = cancel
          Promise.resolve(started).then(
            () => finish(resolve, true),
            (error) => finish(reject, error)
          )
          if (this.destroyed) cancel()
        })
        if (!completed) return false
      }
      if (this.destroyed || operation.cancelled || this.restoreOperation !== operation) return false
      this.audio.pause()
      this.audio.currentTime = 0
      return true
    } catch (error) {
      if (!operation.cancelled && this.restoreOperation === operation) {
        this.storage?.removeItem(STORAGE_KEY)
      }
      return false
    } finally {
      if (this.restoreOperation === operation) {
        this.audio.volume = operation.originalVolume
        this.restoreOperation = null
      }
    }
  }

  async activate() {
    if (this.destroyed) return false

    this.cancelRestore()
    const alertPlayback = this.playAlert()
    this.voice?.initializePermissions?.()
    try {
      if (this.destroyed) return false
      await alertPlayback
    } catch (error) {
      if (this.destroyed) return false
      throw activationError('alert', error)
    }
    if (this.destroyed) return false
    try {
      await this.speak('Painel ativado com som.')
    } catch (error) {
      if (this.destroyed) return false
      throw activationError('voice', error)
    }
    if (this.destroyed) return false
    this.queueBlocked = false
    this.storage?.setItem(STORAGE_KEY, 'true')
    return true
  }

  cancelRestore() {
    const operation = this.restoreOperation
    if (!operation) return

    this.restoreOperation = null
    operation.cancelled = true
    this.audio.volume = operation.originalVolume
    operation.cancel?.()
  }

  enqueue(paciente) {
    if (this.destroyed || this.queueBlocked) return Promise.resolve(false)

    return new Promise((resolve) => {
      this.queue.push({ paciente, resolve })
      this.processQueue()
    })
  }

  async processQueue() {
    if (this.processing || this.destroyed) return
    this.processing = true

    while (this.queue.length && !this.destroyed && !this.queueBlocked) {
      const item = this.queue.shift()
      this.activeItem = item
      try {
        await this.playAlert()
        if (this.destroyed) throw new Error('Audio manager destroyed')
        await this.speak(this.messageFor(item.paciente))
        item.resolve(!this.destroyed)
      } catch (error) {
        this.queueBlocked = true
        item.resolve(false)
        while (this.queue.length) this.queue.shift().resolve(false)
      } finally {
        if (this.activeItem === item) this.activeItem = null
      }
    }

    this.processing = false
  }

  messageFor(paciente) {
    if (!paciente.sala) return paciente.paciente
    return `${paciente.paciente}, dirija-se para sala ${paciente.sala}`
  }

  playAlert() {
    return new Promise((resolve, reject) => {
      let settled = false
      let timer = null
      const finish = (callback, value) => {
        if (settled) return
        settled = true
        this.clearTimer(timer)
        this.audio.removeEventListener('ended', onEnded)
        this.audio.removeEventListener('error', onError)
        if (this.activeTimer === timer) this.activeTimer = null
        if (this.cancelActive === cancel) this.cancelActive = null
        callback(value)
      }
      const onEnded = () => finish(resolve, true)
      const onError = (error) => finish(reject, error)
      const cancel = () => finish(reject, new Error('Audio cancelled'))

      this.audio.currentTime = 0
      this.audio.addEventListener('ended', onEnded)
      this.audio.addEventListener('error', onError)
      timer = this.setTimer(
        () => finish(reject, new Error('Audio timeout')),
        10000
      )
      this.activeTimer = timer
      this.cancelActive = cancel

      try {
        const started = this.audio.play()
        started?.catch?.(onError)
      } catch (error) {
        onError(error)
      }
    })
  }

  speak(text) {
    if (!this.voice?.speak) return Promise.reject(new Error('Voice unavailable'))

    return new Promise((resolve, reject) => {
      let settled = false
      let timer = null
      const finish = (callback, value) => {
        if (settled) return
        settled = true
        this.clearTimer(timer)
        if (this.activeTimer === timer) this.activeTimer = null
        if (this.cancelActive === cancel) this.cancelActive = null
        callback(value)
      }
      const fail = (error) => {
        if (settled) return
        this.voice.cancel?.()
        finish(reject, error)
      }
      const cancel = () => finish(reject, new Error('Voice cancelled'))

      timer = this.setTimer(
        () => fail(new Error('Voice timeout')),
        20000
      )
      this.activeTimer = timer
      this.cancelActive = cancel
      try {
        this.voice.speak(text, VOICE_NAME, {
          onend: () => finish(resolve, true),
          onerror: fail
        })
      } catch (error) {
        fail(error)
      }
    })
  }

  destroy() {
    this.destroyed = true
    this.cancelRestore()
    this.cancelActive?.()
    this.clearTimer(this.activeTimer)
    this.activeItem?.resolve(false)
    this.audio.pause()
    this.voice?.cancel?.()
    while (this.queue.length) this.queue.shift().resolve(false)
  }
}
